import { useState } from 'react';
import api from '../services/api';
import {
  LuPlus, LuPencil, LuTrash2, LuArrowUp, LuArrowDown, LuCheck, LuListChecks, LuHelpCircle, LuX,
} from 'react-icons/lu';
import { QUESTION_TYPES, Modal, errorMessage } from './trainingUtils';

const emptyAssessment = {
  title: '', description: '', module_id: '', passing_score: 70, max_attempts: '', time_limit_minutes: '',
  shuffle_questions: false, shuffle_options: false, show_feedback: true,
};

function AssessmentModal({ course, editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => (editing ? {
    ...emptyAssessment,
    ...editing,
    module_id: editing.module_id ?? '',
    max_attempts: editing.max_attempts ?? '',
    time_limit_minutes: editing.time_limit_minutes ?? '',
    description: editing.description || '',
  } : emptyAssessment));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Informe o título da avaliação.'); return; }
    setSaving(true); setError('');
    const payload = {
      title: form.title,
      description: form.description,
      module_id: form.module_id === '' ? null : Number(form.module_id),
      passing_score: Number(form.passing_score),
      max_attempts: form.max_attempts === '' ? null : Number(form.max_attempts),
      time_limit_minutes: form.time_limit_minutes === '' ? null : Number(form.time_limit_minutes),
      shuffle_questions: form.shuffle_questions,
      shuffle_options: form.shuffle_options,
      show_feedback: form.show_feedback,
    };
    try {
      if (editing) await api.put(`/training/assessments/${editing.id}`, payload);
      else await api.post(`/training/courses/${course.id}/assessments`, payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  return (
    <Modal title={editing ? 'Editar avaliação' : 'Nova avaliação'} icon={<LuListChecks color="var(--accent)" />} onClose={onClose} maxWidth={640}>
      {error && <div className="alert-custom danger">{error}</div>}
      <div>
        <label className="form-label">Título *</label>
        <input className="form-control" value={form.title} autoFocus maxLength={200} onChange={(e) => set('title', e.target.value)} />
      </div>
      <div>
        <label className="form-label">Instruções</label>
        <textarea className="form-control" rows={2} value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>
      <div className="training-form-grid">
        <div>
          <label className="form-label">Vínculo</label>
          <select className="form-control" value={form.module_id} onChange={(e) => set('module_id', e.target.value)}>
            <option value="">Prova final do curso</option>
            {course.modules.map((m, i) => <option key={m.id} value={m.id}>Módulo {i + 1}: {m.title}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Nota mínima (0–100)</label>
          <input className="form-control" type="number" min="0" max="100" value={form.passing_score} onChange={(e) => set('passing_score', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Máx. de tentativas</label>
          <input className="form-control" type="number" min="1" value={form.max_attempts} placeholder="Ilimitado" onChange={(e) => set('max_attempts', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Tempo limite (min)</label>
          <input className="form-control" type="number" min="1" value={form.time_limit_minutes} placeholder="Sem limite" onChange={(e) => set('time_limit_minutes', e.target.value)} />
        </div>
      </div>
      <label className="training-check"><input type="checkbox" checked={form.shuffle_questions} onChange={(e) => set('shuffle_questions', e.target.checked)} /> Embaralhar questões</label>
      <label className="training-check"><input type="checkbox" checked={form.shuffle_options} onChange={(e) => set('shuffle_options', e.target.checked)} /> Embaralhar alternativas</label>
      <label className="training-check"><input type="checkbox" checked={form.show_feedback} onChange={(e) => set('show_feedback', e.target.checked)} /> Exibir gabarito e explicações após o envio</label>
      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}><LuCheck /> {saving ? 'Salvando…' : 'Salvar'}</button>
      </div>
    </Modal>
  );
}

const defaultOptions = (type) => (type === 'true_false'
  ? [{ text: 'Verdadeiro', is_correct: true }, { text: 'Falso', is_correct: false }]
  : [{ text: '', is_correct: false }, { text: '', is_correct: false }, { text: '', is_correct: false }]);

function QuestionModal({ assessmentId, editing, onClose, onSaved }) {
  const [form, setForm] = useState(() => (editing
    ? { type: editing.type, statement: editing.statement, weight: editing.weight, explanation: editing.explanation || '', options: editing.options.map((o) => ({ ...o })) }
    : { type: 'single', statement: '', weight: 1, explanation: '', options: defaultOptions('single') }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleType = (type) => {
    setForm((f) => {
      let options = f.options;
      if (type === 'true_false') options = defaultOptions('true_false');
      else if (f.type === 'true_false') options = defaultOptions(type);
      else if (type === 'single' && options.filter((o) => o.is_correct).length > 1) {
        let first = true;
        options = options.map((o) => { const keep = o.is_correct && first; if (o.is_correct) first = false; return { ...o, is_correct: keep }; });
      }
      return { ...f, type, options };
    });
  };

  const setOption = (i, patch) => setForm((f) => ({ ...f, options: f.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) }));
  const markCorrect = (i, checked) => setForm((f) => ({
    ...f,
    options: f.options.map((o, j) => {
      if (f.type === 'multiple') return j === i ? { ...o, is_correct: checked } : o;
      return { ...o, is_correct: j === i };
    }),
  }));

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = { ...form, weight: Number(form.weight) };
      if (editing) await api.put(`/training/questions/${editing.id}`, payload);
      else await api.post(`/training/assessments/${assessmentId}/questions`, payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e)); setSaving(false);
    }
  };

  const isTF = form.type === 'true_false';

  return (
    <Modal title={editing ? 'Editar questão' : 'Nova questão'} icon={<LuHelpCircle color="var(--accent)" />} onClose={onClose} maxWidth={700}>
      {error && <div className="alert-custom danger">{error}</div>}
      <div className="training-form-grid">
        <div>
          <label className="form-label">Tipo</label>
          <select className="form-control" value={form.type} onChange={(e) => handleType(e.target.value)}>
            {Object.entries(QUESTION_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Peso</label>
          <input className="form-control" type="number" min="0.1" step="0.5" value={form.weight} onChange={(e) => set('weight', e.target.value)} />
        </div>
      </div>
      <div>
        <label className="form-label">Enunciado *</label>
        <textarea className="form-control" rows={3} value={form.statement} autoFocus onChange={(e) => set('statement', e.target.value)} />
      </div>
      <div>
        <label className="form-label">
          Alternativas — marque {form.type === 'multiple' ? 'todas as corretas' : 'a correta'}
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {form.options.map((o, i) => (
            <div key={o.id || `new-${i}`} className="training-option-row">
              <input type={form.type === 'multiple' ? 'checkbox' : 'radio'} name="correct" checked={o.is_correct}
                title="Alternativa correta" style={{ width: 18, height: 18, accentColor: 'var(--accent)', flexShrink: 0 }}
                onChange={(e) => markCorrect(i, e.target.checked)} />
              <input className="form-control" value={o.text} placeholder={`Alternativa ${i + 1}`}
                onChange={(e) => setOption(i, { text: e.target.value })} />
              {!isTF && (
                <button type="button" className="training-icon-btn training-icon-btn--danger" title="Remover alternativa" disabled={form.options.length <= 2}
                  onClick={() => setForm((f) => ({ ...f, options: f.options.filter((_, j) => j !== i) }))}><LuX /></button>
              )}
            </div>
          ))}
        </div>
        {!isTF && (
          <button type="button" className="training-btn training-btn--sm" style={{ marginTop: 8 }}
            onClick={() => setForm((f) => ({ ...f, options: [...f.options, { text: '', is_correct: false }] }))}>
            <LuPlus /> Alternativa
          </button>
        )}
      </div>
      <div>
        <label className="form-label">Explicação (opcional)</label>
        <textarea className="form-control" rows={2} value={form.explanation} placeholder="Mostrada ao aluno depois do envio, se a avaliação exibir o gabarito"
          onChange={(e) => set('explanation', e.target.value)} />
      </div>
      <div className="training-modal-footer">
        <button type="button" className="training-btn" onClick={onClose}>Cancelar</button>
        <button type="button" className="training-btn training-btn--primary" onClick={handleSave} disabled={saving}><LuCheck /> {saving ? 'Salvando…' : 'Salvar questão'}</button>
      </div>
    </Modal>
  );
}

export default function TrainingAssessmentsTab({ course, onChanged }) {
  const [assessmentModal, setAssessmentModal] = useState(null);
  const [questionModal, setQuestionModal] = useState(null); // { assessmentId, editing }
  const [error, setError] = useState('');
  const locked = course.has_students;
  const lockTitle = locked ? 'Curso com alunos: o conteúdo não pode ser excluído' : undefined;

  const run = async (fn) => {
    setError('');
    try { await fn(); onChanged(); } catch (e) { setError(errorMessage(e, 'Não foi possível concluir a ação')); }
  };

  const moduleLabel = (a) => {
    if (!a.module_id) return 'Prova final';
    const i = course.modules.findIndex((m) => m.id === a.module_id);
    return i >= 0 ? `Módulo ${i + 1}: ${course.modules[i].title}` : 'Módulo';
  };

  const handleMoveQuestion = (a, index, dir) => {
    const ids = a.questions.map((q) => q.id);
    [ids[index], ids[index + dir]] = [ids[index + dir], ids[index]];
    run(() => api.put(`/training/assessments/${a.id}/questions/reorder`, { ids }));
  };

  const totalWeight = (a) => a.questions.reduce((s, q) => s + Number(q.weight), 0);

  return (
    <div>
      {error && <div className="alert-custom danger">{error}</div>}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
        <div className="training-hint" style={{ marginTop: 0 }}>
          Nota = (soma dos pesos das questões certas ÷ soma de todos os pesos) × 100. Múltipla escolha só conta se o aluno marcar exatamente as corretas.
        </div>
        <button type="button" className="training-btn training-btn--primary" onClick={() => setAssessmentModal('new')}><LuPlus /> Nova avaliação</button>
      </div>

      {!course.assessments.length && (
        <div className="empty-state"><LuListChecks size={32} color="var(--text-3)" /><p>Nenhuma avaliação cadastrada</p><small>Avaliações são opcionais; sem elas o aluno é aprovado ao concluir as aulas obrigatórias.</small></div>
      )}

      {course.assessments.map((a) => (
        <div key={a.id} className="training-block">
          <div className="training-block-header">
            <div>
              <div className="training-block-title"><LuListChecks color="var(--accent)" /> {a.title}</div>
              <div className="training-row-meta">
                <span>{moduleLabel(a)}</span>
                <span>Mínima {a.passing_score}</span>
                <span>{a.max_attempts ? `${a.max_attempts} tentativa${a.max_attempts > 1 ? 's' : ''}` : 'Tentativas ilimitadas'}</span>
                <span>{a.time_limit_minutes ? `${a.time_limit_minutes} min` : 'Sem tempo limite'}</span>
                {a.shuffle_questions && <span>Questões embaralhadas</span>}
                {a.shuffle_options && <span>Alternativas embaralhadas</span>}
                <span>{a.show_feedback ? 'Mostra gabarito após envio' : 'Não mostra gabarito'}</span>
              </div>
            </div>
            <div className="training-row-actions">
              <button type="button" className="training-icon-btn" title="Editar avaliação" onClick={() => setAssessmentModal(a)}><LuPencil /></button>
              <button type="button" className="training-icon-btn training-icon-btn--danger" title={lockTitle || 'Excluir avaliação'} disabled={locked}
                onClick={() => window.confirm(`Excluir a avaliação "${a.title}" e todas as questões?`) && run(() => api.delete(`/training/assessments/${a.id}`))}><LuTrash2 /></button>
            </div>
          </div>
          {!a.questions.length && <div className="training-empty-row">Nenhuma questão — a avaliação precisa de ao menos 1 questão para o curso ser publicado.</div>}
          {a.questions.map((q, qi) => (
            <div key={q.id} className="training-row">
              <div className="training-row-main">
                <div className="training-row-title">{qi + 1}. {q.statement}</div>
                <div className="training-row-meta">
                  <span>{QUESTION_TYPES[q.type]}</span>
                  <span>Peso {Number(q.weight).toLocaleString('pt-BR')} ({totalWeight(a) ? Math.round((Number(q.weight) / totalWeight(a)) * 100) : 0}% da nota)</span>
                  <span>Correta{q.options.filter((o) => o.is_correct).length > 1 ? 's' : ''}: {q.options.filter((o) => o.is_correct).map((o) => o.text).join(' · ')}</span>
                </div>
              </div>
              <div className="training-row-actions">
                <button type="button" className="training-icon-btn" title="Subir" disabled={qi === 0} onClick={() => handleMoveQuestion(a, qi, -1)}><LuArrowUp /></button>
                <button type="button" className="training-icon-btn" title="Descer" disabled={qi === a.questions.length - 1} onClick={() => handleMoveQuestion(a, qi, 1)}><LuArrowDown /></button>
                <button type="button" className="training-icon-btn" title="Editar questão" onClick={() => setQuestionModal({ assessmentId: a.id, editing: q })}><LuPencil /></button>
                <button type="button" className="training-icon-btn training-icon-btn--danger" title={lockTitle || 'Excluir questão'} disabled={locked}
                  onClick={() => window.confirm('Excluir esta questão?') && run(() => api.delete(`/training/questions/${q.id}`))}><LuTrash2 /></button>
              </div>
            </div>
          ))}
          <div className="training-row">
            <button type="button" className="training-btn training-btn--sm" onClick={() => setQuestionModal({ assessmentId: a.id, editing: null })}><LuPlus /> Nova questão</button>
          </div>
        </div>
      ))}

      {assessmentModal && (
        <AssessmentModal course={course} editing={assessmentModal === 'new' ? null : assessmentModal}
          onClose={() => setAssessmentModal(null)} onSaved={() => { setAssessmentModal(null); onChanged(); }} />
      )}
      {questionModal && (
        <QuestionModal assessmentId={questionModal.assessmentId} editing={questionModal.editing}
          onClose={() => setQuestionModal(null)} onSaved={() => { setQuestionModal(null); onChanged(); }} />
      )}
    </div>
  );
}
